import React, { useMemo } from 'react';
import {
    PieChart, Pie, Cell,
    BarChart, Bar,
    LineChart, Line,
    AreaChart, Area,
    RadialBarChart, RadialBar,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { detectChartKeys, type ChartSeriesInput } from '../../../../../utils/chartUtils';
import { VizSwitcher, type VizType } from './VizSwitcher';
import { useTranslation } from '../../../../../contexts/I18nProvider';

interface ChartSeries {
    name: string;
    data: Record<string, any>[];
    color?: string;
    dataKey?: string;
}

interface ChartAxisConfig {
    label?: string;
    dataKey: string;
    format?: string;
}

interface ChartData {
    chartType: string;
    title: string;
    series: ChartSeries[];
    xAxis?: ChartAxisConfig;
    yAxis?: ChartAxisConfig;
}

interface ChartBlockProps {
    data: ChartData;
    /** Callback for viz type switching */
    onVizSwitch?: (type: VizType) => void;
    /** Currently active viz type (for VizSwitcher highlight) */
    currentVizType?: VizType;
    /** Callback for drill-down when user clicks a data point */
    onDataPointClick?: (label: string, value: number) => void;
    /** Fargepalett fra temaet (Compass-visninger). Uten den brukes standardfargene. */
    palette?: string[];
    /** Skjul tittelen (panelet viser den selv). */
    embedded?: boolean;
    /** Høyde i piksler (standard 300). */
    height?: number;
}

/** Kutter lange kategorinavn på aksen (hele navnet vises i verktøytipset). */
const truncateLabel = (value: unknown, maxChars: number): string => {
    const text = value === null || value === undefined ? '' : String(value);
    return text.length > maxChars ? `${text.slice(0, Math.max(1, maxChars - 1))}…` : text;
};

/** Omtrentlig tegnbredde i px for 11 px skrift — brukes til å dimensjonere kategoriaksen. */
const CHAR_PX = 6.2;

const COLORS = [
    '#3b82f6', '#8b5cf6', '#10b981', '#f59e0b',
    '#ef4444', '#06b6d4', '#ec4899', '#6366f1',
];

export const ChartBlock: React.FC<ChartBlockProps> = ({ data, onVizSwitch, currentVizType, onDataPointClick, palette, embedded, height: heightProp }) => {
    const colors = palette && palette.length > 0 ? palette : COLORS;
    const { t } = useTranslation();
    const activeType = currentVizType || (data.chartType?.toLowerCase() as VizType) || 'bar';
    const height = heightProp ?? 300;

    const { xKey, yKeys, flatData, isMultiSeries: multi } = useMemo(
        () => detectChartKeys(
            data.series?.[0]?.data || [],
            data.series as ChartSeriesInput[],
            data.xAxis,
            data.yAxis
        ),
        [data.series, data.xAxis, data.yAxis]
    );

    if (!flatData.length) {
        return <p className="text-sm text-gray-500 py-4">{t('manager.modules.claudeQuery.chart.noData', {}, 'Ingen data for diagrammet')}</p>;
    }

    /** Find the color for a given yKey from series config */
    const getColor = (key: string, idx: number): string => {
        const match = data.series.find(s => (s.dataKey || s.name) === key);
        return match?.color || colors[idx % colors.length];
    };

    /** Find the display name for a given yKey from series config */
    const getName = (key: string): string => {
        const match = data.series.find(s => (s.dataKey || s.name) === key);
        return match?.name || key;
    };

    // Én serie: tittelen sier hva tallene er, og legenden tok plass (og ble kuttet i små paneler)
    const showLegend = yKeys.length > 1;

    const renderChart = () => {
        if (activeType === 'pie') {
            const pieData = data.series?.[0]?.data || flatData;
            const pieYKey = yKeys[0];
            return (
                <ResponsiveContainer width="100%" height={height}>
                    <PieChart>
                        <Pie
                            data={pieData}
                            dataKey={pieYKey}
                            nameKey={xKey}
                            cx="50%"
                            cy="50%"
                            outerRadius={100}
                            label
                        >
                            {pieData.map((_entry, index) => (
                                <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                            ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                    </PieChart>
                </ResponsiveContainer>
            );
        }

        if (activeType === 'bar') {
            const handleBarClick = onDataPointClick
                ? (entry: any) => {
                    if (entry && entry.activePayload?.[0]) {
                        const payload = entry.activePayload[0].payload;
                        const label = String(payload[xKey] || '');
                        const value = payload[yKeys[0]] ?? 0;
                        onDataPointClick(label, value);
                    }
                }
                : undefined;

            return (
                <ResponsiveContainer width="100%" height={height}>
                    <BarChart
                        data={flatData}
                        onClick={handleBarClick}
                        style={onDataPointClick ? { cursor: 'pointer' } : undefined}
                    >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey={xKey} tick={{ fontSize: 11 }} interval={0} angle={-30} textAnchor="end" height={60} tickFormatter={(v) => truncateLabel(v, 16)} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip />
                        {showLegend && <Legend />}
                        {yKeys.map((key, idx) => (
                            <Bar
                                key={key}
                                dataKey={key}
                                fill={getColor(key, idx)}
                                name={getName(key)}
                            />
                        ))}
                    </BarChart>
                </ResponsiveContainer>
            );
        }

        if (activeType === 'stacked-bar') {
            return (
                <ResponsiveContainer width="100%" height={height}>
                    <BarChart data={flatData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey={xKey} tick={{ fontSize: 11 }} interval={0} angle={-30} textAnchor="end" height={60} tickFormatter={(v) => truncateLabel(v, 16)} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip />
                        {showLegend && <Legend />}
                        {yKeys.map((key, idx) => (
                            <Bar
                                key={key}
                                dataKey={key}
                                fill={getColor(key, idx)}
                                name={getName(key)}
                                stackId="stack"
                            />
                        ))}
                    </BarChart>
                </ResponsiveContainer>
            );
        }

        if (activeType === 'horizontal-bar') {
            // Kategoriaksen får bredde etter de lengste navnene (maks 200 px); lengre navn kuttes med «…»,
            // så de ikke brytes over flere linjer og skrives oppå hverandre.
            const longest = flatData.reduce((max, d) => Math.max(max, String(d[xKey] ?? '').length), 0);
            const labelWidth = Math.min(200, Math.max(60, Math.round(longest * CHAR_PX) + 12));
            const maxChars = Math.floor((labelWidth - 12) / CHAR_PX);
            // Minst 18 px per stolpe (topp 20 får plass i et vanlig panel); flere rader gir rulling i panelet
            const chartHeight = Math.max(height, flatData.length * 18 + 30);
            return (
                <ResponsiveContainer width="100%" height={chartHeight}>
                    <BarChart data={flatData} layout="vertical" barCategoryGap="20%">
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" tick={{ fontSize: 12 }} />
                        <YAxis type="category" dataKey={xKey} tick={{ fontSize: 11 }} width={labelWidth} interval={0}
                            tickFormatter={(v) => truncateLabel(v, maxChars)} />
                        <Tooltip />
                        {showLegend && <Legend />}
                        {yKeys.map((key, idx) => (
                            <Bar
                                key={key}
                                dataKey={key}
                                fill={getColor(key, idx)}
                                name={getName(key)}
                            />
                        ))}
                    </BarChart>
                </ResponsiveContainer>
            );
        }

        if (activeType === 'gauge') {
            // Gauge works best with a single value (0-100 range)
            const gaugeValue = typeof flatData[0][yKeys[0]] === 'number'
                ? flatData[0][yKeys[0]]
                : parseFloat(flatData[0][yKeys[0]]) || 0;
            const clampedValue = Math.min(100, Math.max(0, gaugeValue));

            const gaugeData = [
                { name: getName(yKeys[0]), value: clampedValue, fill: colors[0] },
            ];

            return (
                <div className="flex flex-col items-center">
                    <ResponsiveContainer width="100%" height={220}>
                        <RadialBarChart
                            cx="50%"
                            cy="50%"
                            innerRadius="60%"
                            outerRadius="90%"
                            data={gaugeData}
                            startAngle={180}
                            endAngle={0}
                            barSize={20}
                        >
                            <RadialBar
                                dataKey="value"
                                cornerRadius={10}
                                background={{ fill: '#f3f4f6' }}
                            />
                            <Tooltip />
                        </RadialBarChart>
                    </ResponsiveContainer>
                    <div className="text-center -mt-16 mb-4">
                        <p className="text-3xl font-bold text-gray-900">{gaugeValue}%</p>
                        <p className="text-xs text-gray-500">{getName(yKeys[0])}</p>
                    </div>
                </div>
            );
        }

        if (activeType === 'line') {
            return (
                <ResponsiveContainer width="100%" height={height}>
                    <LineChart data={flatData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey={xKey} tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip />
                        {showLegend && <Legend />}
                        {yKeys.map((key, idx) => (
                            <Line
                                key={key}
                                type="monotone"
                                dataKey={key}
                                stroke={getColor(key, idx)}
                                name={getName(key)}
                            />
                        ))}
                    </LineChart>
                </ResponsiveContainer>
            );
        }

        if (activeType === 'area') {
            return (
                <ResponsiveContainer width="100%" height={height}>
                    <AreaChart data={flatData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey={xKey} tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip />
                        {showLegend && <Legend />}
                        {yKeys.map((key, idx) => (
                            <Area
                                key={key}
                                type="monotone"
                                dataKey={key}
                                stroke={getColor(key, idx)}
                                fill={getColor(key, idx)}
                                fillOpacity={0.6}
                                name={getName(key)}
                            />
                        ))}
                    </AreaChart>
                </ResponsiveContainer>
            );
        }

        return <p className="text-sm text-gray-500">{t('manager.modules.claudeQuery.chart.unknownType', { '0': activeType }, `Ukjent graf-type: ${activeType}`)}</p>;
    };

    return (
        <div className={embedded ? '' : 'mt-2'}>
            <div className={`flex items-center justify-between ${embedded && !onVizSwitch ? 'hidden' : 'mb-2'}`}>
                {data.title && !embedded ? (
                    <h4 className="text-sm font-semibold text-gray-700">{data.title}</h4>
                ) : (
                    <div />
                )}
                {onVizSwitch && (
                    <VizSwitcher
                        currentType={activeType}
                        onSwitch={onVizSwitch}
                        sourceBlockType="chart"
                        iconOnly={embedded}
                    />
                )}
            </div>
            {renderChart()}
        </div>
    );
};
